import React from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNowStrict } from 'date-fns';
import classnames from 'classnames';
import { trackLinkClick } from '@atlassian/temp-analytics';
import { Invoice, InvoiceBillAndShipTo, Maybe } from '@css-xp/graphtypes/src/generated-types-new';
import { NoData } from '@css-xp/ppc-common';
import { MILLISECONDS_IN_DAY } from '../../../../constants/widget';
import './widget-items.scss';

type Props = {
  url: string;
  company: InvoiceBillAndShipTo['fullName'];
  timestamp?: Maybe<string>;
  timestampPrefix?: React.ReactNode;
  timestampClassName?: string;
  testId?: string;
  belowDayTimestampContent?: React.ReactNode;
  heading?: string;
} & Pick<Invoice, 'invoiceNumber'>;
/**
 * Component for Invoice Widget Item on the Home page
 * @constructor
 */
const InvoiceWidgetItemComponent = ({
  url,
  invoiceNumber,
  timestamp,
  company,
  timestampPrefix = '',
  timestampClassName,
  belowDayTimestampContent,
  testId,
  heading = '',
}: Props) => {
  const renderTimestamp = () => {
    if (!timestamp) {
      return <NoData />;
    }

    return (
      <div className={classnames('timestamp', timestampClassName)} data-testid="invoice-timestamp">
        {belowDayTimestampContent && new Date().getTime() - new Date(timestamp).getTime() <= MILLISECONDS_IN_DAY ? (
          belowDayTimestampContent
        ) : (
          <>
            {timestampPrefix} {formatDistanceToNowStrict(new Date(timestamp), { addSuffix: true })}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="flex align-center justify-space-between mb-12 pv-12" data-testid={testId}>
      <div className="left-side">
        <div className="content">
          <Link
            to={url}
            onClick={() => {
              trackLinkClick('widgetItemClick', { source: heading });
            }}
          >
            <div className="title line-ellipsis">{invoiceNumber}</div>
          </Link>
        </div>
        <div className="content">
          <div className="sub-title line-ellipsis">{company}</div>
        </div>
      </div>
      <div className="right-side">
        <div className="content">{renderTimestamp()}</div>
      </div>
    </div>
  );
};

export const InvoiceWidgetItem = React.memo(InvoiceWidgetItemComponent);
